import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 active:scale-[0.98]",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-teal/10 hover:text-accent-foreground hover:border-teal/50",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-teal/10 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Accent des maquettes Stitch : les actions IA sont indigo.
        accent: "bg-iris text-white shadow-sm hover:bg-iris-soft active:scale-[0.98]",
      },
      // Sous md (téléphone, tablette portrait), toute taille garantit une
      // cible tactile de 44px (lot Simplicité S1) ; la densité desktop reste.
      size: {
        default: "h-9 px-4 py-2 max-md:min-h-11",
        sm: "h-8 rounded-md px-3 text-xs max-md:min-h-11",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-6 text-base",
        icon: "h-9 w-9 max-md:min-h-11 max-md:min-w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
